// Map between Swedish (default) URL slugs and English aliases.
// Only top-level slugs differ; IATA paths (airport, flight, route) are language-neutral.

export type Lang = "sv" | "en";

export const SUPPORTED_LANGS: Lang[] = ["sv", "en"];
export const DEFAULT_LANG: Lang = "sv";

// Top-level slug pairs. Add more as needed.
const ALIAS_SV_TO_EN: Record<string, string> = {
  spel: "game",
  reseservice: "travel-services",
  flygradar: "flight-radar",
  live: "live",
  integritetspolicy: "privacy",
  villkor: "terms",
  cookies: "cookies",
};
// Second-segment aliases (only used under specific top-level slugs).
const SUB_ALIAS_SV_TO_EN: Record<string, Record<string, string>> = {
  flygradar: { sverige: "sweden", europa: "europe" },
};
const SUB_ALIAS_EN_TO_SV: Record<string, Record<string, string>> = {
  "flight-radar": { sweden: "sverige", europe: "europa" },
};
const ALIAS_EN_TO_SV: Record<string, string> = Object.entries(ALIAS_SV_TO_EN)
  .reduce<Record<string, string>>((acc, [sv, en]) => { acc[en] = sv; return acc; }, {});

/** Strip leading /en if present. Returns { lang, rest } where rest starts with "/". */
export function parseLangPath(pathname: string): { lang: Lang; rest: string } {
  const m = pathname.match(/^\/en(\/|$)/);
  if (m) {
    const rest = pathname.slice(3) || "/";
    return { lang: "en", rest: rest.startsWith("/") ? rest : "/" + rest };
  }
  return { lang: "sv", rest: pathname || "/" };
}

/** Translate a path between languages, swapping aliased slugs and prefix. */
export function translatePath(pathname: string, target: Lang): string {
  const { lang, rest } = parseLangPath(pathname);
  if (lang === target) return pathname;
  // Swap first segment if aliased
  const segs = rest.split("/").filter(Boolean);
  if (segs.length > 0) {
    const head = segs[0];
    if (target === "en" && ALIAS_SV_TO_EN[head]) {
      segs[0] = ALIAS_SV_TO_EN[head];
      if (segs[1] && SUB_ALIAS_SV_TO_EN[head]?.[segs[1]]) {
        segs[1] = SUB_ALIAS_SV_TO_EN[head][segs[1]];
      }
    } else if (target === "sv" && ALIAS_EN_TO_SV[head]) {
      const enHead = head;
      segs[0] = ALIAS_EN_TO_SV[head];
      if (segs[1] && SUB_ALIAS_EN_TO_SV[enHead]?.[segs[1]]) {
        segs[1] = SUB_ALIAS_EN_TO_SV[enHead][segs[1]];
      }
    }
  }
  const newRest = "/" + segs.join("/");
  return target === "en" ? "/en" + (newRest === "/" ? "" : newRest) : newRest;
}

/** Build a localized href from a canonical Swedish path, given current language. */
export function localizedHref(svPath: string, lang: Lang): string {
  if (lang === "sv") return svPath;
  return translatePath(svPath, "en");
}

/** Get alternate URLs for hreflang tags. */
export function alternateUrls(svPath: string, origin = "https://flyg.info") {
  const enPath = translatePath(svPath, "en");
  return {
    sv: `${origin}${svPath}`,
    en: `${origin}${enPath}`,
  };
}